import { generatePath, Link as RouterLink, useParams } from 'react-router-dom';

import { Box, Breadcrumbs, Card, CardActions, CardContent, Link, Table, TableBody } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { breadcrumbIconSx, breadcrumbLinkSx, breadcrumbsContainerSx, breadcrumbsSx } from 'common/styles';

import { DetailsCardTitle } from 'commonComponents/DetailsCardTitle';
import { LinkButton } from 'commonComponents/LinkButton';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { PageContainer } from 'commonComponents/PageContainer';

import { useUser } from 'api/authorization';
import { loadUserDetails } from 'api/users';
import { EditIcon, UndoIcon } from 'icons';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { UserDeleteButton } from './UserDeleteButton';
import { UserDetailsRows } from './UserDetailsRows';
import { UserInvalidateButton } from './UserInvalidateButton';
import { otherAdminUserIsLoggedInUser } from './utils';

export function UserDetails() {
    const { data: loggedInUser } = useUser();
    const { id: userId } = useParams<{ id: string }>();

    const { data: user, error } = useQuery({
        queryKey: ['User', 'detail', userId],
        queryFn: () => loadUserDetails(Number(userId)),
        meta: { errorMessage: 'Loading user failed.' },
    });

    return (
        <PageContainer
            maxWidth="md"
            sx={{
                '& .MuiContainer-root': {
                    paddingTop: 0,
                },
            }}
        >
            <Box sx={breadcrumbsContainerSx}>
                <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbsSx}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.USER_LIST}
                        underline="hover"
                        data-testid="UserDetails_BreadcrumbUsers"
                        sx={breadcrumbLinkSx}
                    >
                        <UndoIcon sx={breadcrumbIconSx} />
                        Users
                    </Link>
                </Breadcrumbs>
            </Box>
            {error ? null : (
                <>
                    {!user ? (
                        <LoadingIndicator />
                    ) : (
                        <Card>
                            <CardContent data-testid="UserDetails_CardContent">
                                <DetailsCardTitle
                                    subtitle={`User${user.invalidated ? ' [INVALIDATED]' : ''}`}
                                    title={user.username}
                                    showError={user.invalidated}
                                />
                                <Table size="small">
                                    <TableBody>
                                        <UserDetailsRows user={user} />
                                    </TableBody>
                                </Table>
                            </CardContent>
                            <CardActions>
                                {otherAdminUserIsLoggedInUser(user, loggedInUser) && (
                                    <RoleDependentComponent
                                        userRole={ROLE_DEFINITIONS.Administrator}
                                        component={LinkButton}
                                        iconClass={EditIcon}
                                        route={generatePath(ROUTES.USER_EDIT, { id: user.id.toString() })}
                                        title="Edit"
                                        data-testid="UserDetails_EditButton"
                                    />
                                )}
                                {user.hasLicenses ? (
                                    <UserInvalidateButton user={user} />
                                ) : (
                                    <UserDeleteButton user={user} />
                                )}
                            </CardActions>
                        </Card>
                    )}
                </>
            )}
        </PageContainer>
    );
}
